import React, { useState, useMemo } from 'react';
import {
    Box, Button, Chip, CircularProgress, IconButton, Typography, TextField, InputAdornment,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Modal
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useHistory } from 'react-router-dom';
import { saveAs } from 'file-saver';
import { format } from 'date-fns';
import { FindocsService } from '../../../services/dao/findocs-service';

const isValidDate = (d) => d && !isNaN(new Date(d).getTime());
const fmtDate = (d) => isValidDate(d) ? format(new Date(d), 'MM/dd/yyyy') : null;
const fmtMoney = (v) => v != null ? `$${parseFloat(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null;

const CATEGORY_COLORS = {
    'Credit Card Payments': { bg: '#E8EDF2', color: '#141F2E' },
    'Shopping': { bg: '#ECFDF5', color: '#059669' },
    'Groceries': { bg: '#EFF6FF', color: '#2563EB' },
    'Cash Advances': { bg: '#FFF7ED', color: '#C2410C' },
    'Dining': { bg: '#FBF5EB', color: '#8B6914' },
};
const getCatStyle = (cat) => CATEGORY_COLORS[cat] || { bg: '#F1F5F9', color: '#475569' };

const findocsService = new FindocsService();

const FindocDetailsCard = ({ findoc }) => {
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [pdfOpen, setPdfOpen] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfLoading, setPdfLoading] = useState(false);

    const lineItems = findoc.lineItems || [];
    const filtered = useMemo(() => {
        if (!search) return lineItems;
        const q = search.toLowerCase();
        return lineItems.filter(li =>
            (li.description || '').toLowerCase().includes(q) ||
            (li.category || '').toLowerCase().includes(q)
        );
    }, [lineItems, search]);

    // Compute quick stats from line items
    const stats = useMemo(() => {
        let purchases = 0, payments = 0, other = 0;
        lineItems.forEach(li => {
            const amt = li.amount || 0;
            const cat = (li.category || '').toLowerCase();
            if (cat.includes('payment') || cat.includes('credit')) payments += amt;
            else if (cat.includes('cash advance')) other += amt;
            else purchases += amt;
        });
        return { purchases, payments, other };
    }, [lineItems]);

    const handleExport = () => {
        let tsv = "Date\tCategory\tDescription\tAmount\n";
        lineItems.forEach(li => {
            tsv += `${fmtDate(li.transactionDate) || ''}\t${li.category || ''}\t${li.description || ''}\t${li.amount || ''}\n`;
        });
        saveAs(new Blob([tsv], { type: 'text/tsv' }), `${findoc.institutionName || 'statement'}.tsv`);
    };

    const statementDate = fmtDate(findoc.statementDate);
    const paymentDate = fmtDate(findoc.paymentDate);
    const hasPdf = findoc.meta?.s3Key;

    return (
        <Box sx={{ px: { xs: 2, md: 4 }, py: 4, maxWidth: 1000, mx: 'auto' }}>
            {/* Back button */}
            <Button startIcon={<ArrowBackIcon />} onClick={() => history.goBack()}
                sx={{ mb: 3, color: 'text.secondary', fontWeight: 600, borderRadius: 2, textTransform: 'none',
                    '&:hover': { background: 'rgba(99,102,241,0.06)', color: 'primary.main' } }}>
                Back
            </Button>

            {/* Summary card */}
            <Paper variant="outlined" sx={{ borderRadius: 3, p: 3, mb: 3, border: '1px solid rgba(0,0,0,0.06)' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    {/* Bank icon */}
                    <Box sx={{ width: 48, height: 48, borderRadius: 2, background: '#E8EDF2',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <AccountBalanceIcon sx={{ color: '#1F2F42', fontSize: '1.5rem' }} />
                    </Box>
                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                            {findoc.institutionName || 'Unknown Institution'}
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
                            {findoc.statementDateRange && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Period: {findoc.statementDateRange}
                                </Typography>
                            )}
                            {paymentDate && (
                                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                    Payment Due: {paymentDate}
                                </Typography>
                            )}
                        </Box>
                        {findoc.institutionAddress && (
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {findoc.institutionAddress}
                            </Typography>
                        )}
                    </Box>
                    {/* Balance */}
                    {findoc.balance != null && (
                        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>Balance</Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700, color: findoc.balance >= 0 ? '#059669' : '#DC2626' }}>
                                {fmtMoney(findoc.balance)}
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Quick stats */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                    {stats.purchases !== 0 && (
                        <Chip label={`Purchases: ${fmtMoney(stats.purchases)}`} size="small"
                            sx={{ fontWeight: 600, background: '#FEF2F2', color: '#DC2626' }} />
                    )}
                    {stats.payments !== 0 && (
                        <Chip label={`Payments/Credits: ${fmtMoney(stats.payments)}`} size="small"
                            sx={{ fontWeight: 600, background: '#ECFDF5', color: '#059669' }} />
                    )}
                    {stats.other !== 0 && (
                        <Chip label={`Cash Advances: ${fmtMoney(stats.other)}`} size="small"
                            sx={{ fontWeight: 600, background: '#FFF7ED', color: '#C2410C' }} />
                    )}
                </Box>
            </Paper>

            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField size="small" placeholder="Search transactions..." value={search}
                    onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 200 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} /></InputAdornment> }} />
                <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={handleExport}
                    sx={{ borderRadius: 2, textTransform: 'none' }}>Export CSV</Button>
                {hasPdf && (
                    <Button variant="outlined" size="small" startIcon={pdfLoading ? <CircularProgress size={14} /> : <PictureAsPdfIcon />}
                        disabled={pdfLoading}
                        onClick={async () => {
                            setPdfLoading(true);
                            const url = await findocsService.getPdfUrl(findoc._id);
                            setPdfUrl(url);
                            setPdfLoading(false);
                            if (url) setPdfOpen(true);
                        }}
                        sx={{ borderRadius: 2, textTransform: 'none' }}>View PDF</Button>
                )}
            </Box>

            {/* Transactions table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 600, fontSize: '0.75rem', color: 'text.secondary', textTransform: 'uppercase', borderBottom: '2px solid rgba(0,0,0,0.08)' } }}>
                            <TableCell>Date</TableCell>
                            <TableCell>Category</TableCell>
                            <TableCell>Description</TableCell>
                            <TableCell align="right">Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.map((li, i) => {
                            const date = fmtDate(li.transactionDate);
                            const amt = li.amount;
                            const catStyle = getCatStyle(li.category);
                            return (
                                <TableRow key={i} sx={{ '&:nth-of-type(even)': { background: '#F8FAFC' }, '&:hover': { background: '#E8EDF2' } }}>
                                    <TableCell sx={{ fontSize: '0.85rem', color: date ? 'text.primary' : 'text.secondary' }}>
                                        {date || '—'}
                                    </TableCell>
                                    <TableCell>
                                        {li.category && (
                                            <Chip label={li.category} size="small"
                                                sx={{ fontSize: '0.7rem', fontWeight: 600, background: catStyle.bg, color: catStyle.color }} />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: '0.85rem', fontWeight: 500 }}>{li.description}</TableCell>
                                    <TableCell align="right" sx={{
                                        fontSize: '0.85rem', fontWeight: 600,
                                        color: amt > 0 ? '#DC2626' : amt < 0 ? '#059669' : 'text.primary',
                                    }}>
                                        {fmtMoney(amt) || '—'}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {/* Totals row */}
                        {findoc.balance != null && (
                            <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid rgba(0,0,0,0.1)', fontSize: '0.85rem' } }}>
                                <TableCell colSpan={3}>Ending Balance</TableCell>
                                <TableCell align="right">{fmtMoney(findoc.balance)}</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 2, textAlign: 'center' }}>
                {filtered.length} transaction{filtered.length !== 1 ? 's' : ''}
                {search && ` matching "${search}"`}
            </Typography>

            {/* PDF viewer */}
            <Modal open={pdfOpen} onClose={() => setPdfOpen(false)}>
                <Box sx={{ position: 'fixed', inset: 0, bgcolor: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconButton sx={{ position: 'fixed', top: 12, right: 12, color: 'white', zIndex: 1 }} onClick={() => setPdfOpen(false)}><CloseIcon /></IconButton>
                    {pdfUrl && <iframe src={pdfUrl} title="Statement PDF" style={{ width: '90%', height: '90%', border: 'none', borderRadius: 8 }} />}
                </Box>
            </Modal>
        </Box>
    );
};

export default FindocDetailsCard;
